/**
 * Loading Screen
 * Full-screen overlay with background image and animated progress bar.
 * Used for initial app load and game mode transitions.
 */

const MIN_DISPLAY_MS = 2000;


export class LoadingScreen {
  private element: HTMLElement | null = null;
  private progressFill: HTMLElement | null = null;
  private showStartTime = 0;
  private fakeProgress = 0;
  private fakeTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Grab the initial #loading element from index.html if it exists
    const existing = document.getElementById('loading');
    if (existing) {
      this.element = existing;
      this.progressFill = existing.querySelector('.loading-progress-fill');
      // Already visible from page load — record start time and animate progress
      this.showStartTime = Date.now();
      this.startFakeProgress();
    }
  }

  /** Show the loading screen. Creates the overlay if not already in the DOM. */
  show(): void {
    if (!this.element) {
      this.createElement();
    }

    const el = this.element!;
    // Re-add to DOM if removed
    if (!el.parentNode) {
      document.getElementById('app')!.appendChild(el);
    }
    el.style.display = 'flex';
    el.style.opacity = '1';
    // Reset progress
    this.setProgress(0);
    this.showStartTime = Date.now();
    this.startFakeProgress();
  }

  /** Hide the loading screen with a fade-out. Resolves when fully hidden. */
  async hide(): Promise<void> {
    console.log('[BOOT] hide() START');
    this.stopFakeProgress();

    if (!this.element) { console.log('[BOOT] hide() no element — return'); return; }

    // Fill to 100%
    this.setProgress(100);

    // Ensure minimum display time
    const elapsed = Date.now() - this.showStartTime;
    console.log('[BOOT] hide() elapsed=' + elapsed + ' (min=' + MIN_DISPLAY_MS + ')');
    if (elapsed < MIN_DISPLAY_MS) {
      await sleep(MIN_DISPLAY_MS - elapsed);
    }
    console.log('[BOOT] hide() past min-display sleep');

    // Brief pause at 100% so user sees it complete
    await sleep(150);
    console.log('[BOOT] hide() past 150ms pause');

    // Fade out
    this.element.style.opacity = '0';
    await sleep(300);
    console.log('[BOOT] hide() past fade — removing element');

    // Remove from DOM so it doesn't block touch events on layers below
    this.element.remove();
    console.log('[BOOT] hide() DONE');
  }

  /** Returns a promise that resolves once the minimum display time has passed. */
  waitForMinTime(): Promise<void> {
    const elapsed = Date.now() - this.showStartTime;
    const remaining = MIN_DISPLAY_MS - elapsed;
    return remaining > 0 ? sleep(remaining) : Promise.resolve();
  }

  /** Remove from DOM entirely. */
  dispose(): void {
    this.stopFakeProgress();
    this.element?.remove();
    this.element = null;
    this.progressFill = null;
  }

  // ── Private ──────────────────────────────────────────────

  private setProgress(pct: number): void {
    this.fakeProgress = pct;
    if (this.progressFill) {
      this.progressFill.style.width = `${pct}%`;
    }
  }

  private startFakeProgress(): void {
    this.stopFakeProgress();
    this.fakeProgress = 0;
    this.setProgress(0);

    // Ease-out toward 80% over ~3s
    this.fakeTimer = setInterval(() => {
      if (this.fakeProgress >= 80) {
        this.stopFakeProgress();
        return;
      }
      // Decelerating increments
      const remaining = 80 - this.fakeProgress;
      const step = Math.max(0.3, remaining * 0.04);
      this.setProgress(Math.min(80, this.fakeProgress + step));
    }, 50);
  }

  private stopFakeProgress(): void {
    if (this.fakeTimer !== null) {
      clearInterval(this.fakeTimer);
      this.fakeTimer = null;
    }
  }

  private createElement(): void {
    const el = document.createElement('div');
    el.id = 'loading';
    el.innerHTML = `
      <div class="loading-progress">
        <div class="loading-progress-fill"></div>
      </div>
    `;
    document.getElementById('app')!.appendChild(el);

    this.element = el;
    this.progressFill = el.querySelector('.loading-progress-fill');
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
